import { useEffect, useState } from 'react';
import { useRunStore, elapsedText } from '../stores/runStore';
import { useUiStore } from '../stores/uiStore';
import Spinner from './Spinner';
import './RunIndicator.css';

// Top bar badge and pop-ups for model runs (2026-10-22). See stores/runStore.ts.

/** Re-renders once a second while `on`, for running timers. */
export function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [on]);
  return now;
}

/** Elapsed time since `startedAt`, ticking. */
export function RunTimer({ startedAt }: { startedAt: number }) {
  const now = useNow(true);
  return <span className="run-timer">{elapsedText(startedAt, now)}</span>;
}

/** The badge beside the spend figure while any run is going; click opens the word. */
export function RunBadge() {
  const runs = useRunStore((s) => s.runs);
  const openContentWord = useUiStore((s) => s.openContentWord);
  const setPage = useUiStore((s) => s.setPage);
  if (runs.length === 0) return null;
  const run = runs[0];
  const more = runs.length > 1 ? ` (${runs.length} running)` : '';
  const open = () => {
    if (run.wordId) openContentWord(run.wordId, run.kind === 'generate' ? run.stage ?? null : null);
    else setPage('wordbank');
  };
  return (
    <button className="run-badge" onClick={open} title={runs.map((r) => `${r.word}, ${r.label}`).join('\n')}>
      <Spinner label="Generating" />
      <span>Generating{more}: {run.word}, {run.label} · </span>
      <RunTimer startedAt={run.startedAt} />
    </button>
  );
}

/** Done or failed pop-ups; each stays until clicked or for 8 seconds. */
export function RunToasts() {
  const toasts = useRunStore((s) => s.toasts);
  const dismiss = useRunStore((s) => s.dismiss);
  if (toasts.length === 0) return null;
  return (
    <div className="run-toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <button key={t.id} className={`run-toast ${t.ok ? 'run-toast-ok' : 'run-toast-fail'}`} onClick={() => dismiss(t.id)} title="Dismiss">
          {t.text}
        </button>
      ))}
    </div>
  );
}
